import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mail, Phone, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { COMPANY_INFO } from '@/lib/index';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setIsSubmitted(false);
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo: Array<{
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    content: string;
    link: string | null;
  }> = [
    {
      icon: MapPin,
      title: 'Localisation',
      content: COMPANY_INFO.location,
      link: null,
    },
    {
      icon: Mail,
      title: 'Email',
      content: COMPANY_INFO.email,
      link: `mailto:${COMPANY_INFO.email}`,
    },
    {
      icon: Phone,
      title: 'Téléphone',
      content: COMPANY_INFO.phone,
      link: `tel:${COMPANY_INFO.phone}`,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <motion.section
        className="relative py-24 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={springPresets.gentle}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-background" />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center mb-16"
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            transition={springPresets.gentle}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Contactez-Nous
            </h1>
            <p className="text-xl text-muted-foreground">
              Transformons ensemble vos espaces en œuvres d'art. Notre équipe est à votre écoute pour concrétiser vos projets de design d'intérieur.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="space-y-8"
            >
              <motion.div variants={staggerItem}>
                <h2 className="text-3xl font-bold mb-6 text-foreground">
                  Informations de Contact
                </h2>
                <p className="text-muted-foreground mb-8">
                  {COMPANY_INFO.description}
                </p>
              </motion.div>

              <motion.div variants={staggerItem} className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  const content = info.link ? (
                    <a
                      href={info.link}
                      className="text-foreground hover:text-primary transition-colors duration-200"
                    >
                      {info.content}
                    </a>
                  ) : (
                    <span className="text-foreground">{info.content}</span>
                  );

                  return (
                    <Card
                      key={index}
                      className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/30 transition-all duration-300"
                      style={{
                        boxShadow: '0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)',
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-primary/10 text-primary">
                          <Icon className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg mb-1">{info.title}</h3>
                          <div className="text-base">{content}</div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </motion.div>

              <motion.div
                variants={staggerItem}
                className="p-8 rounded-2xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/5 border border-primary/20"
              >
                <h3 className="text-xl font-bold mb-3 text-foreground">
                  Horaires d'Ouverture
                </h3>
                <div className="space-y-2 text-muted-foreground">
                  <p>Lundi - Vendredi: 8h00 - 18h00</p>
                  <p>Samedi: 9h00 - 15h00</p>
                  <p>Dimanche: Sur rendez-vous</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              transition={springPresets.gentle}
            >
              <Card
                className="p-8 bg-card/80 backdrop-blur-sm border-border/50"
                style={{
                  boxShadow: '0 8px 30px -6px color-mix(in srgb, var(--primary) 15%, transparent)',
                }}
              >
                <h2 className="text-3xl font-bold mb-6 text-foreground">
                  Envoyez-nous un Message
                </h2>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={springPresets.bouncy}
                    className="py-12 text-center"
                  >
                    <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2 text-foreground">
                      Message Envoyé!
                    </h3>
                    <p className="text-muted-foreground">
                      Nous vous répondrons dans les plus brefs délais.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2 text-foreground">
                        Nom Complet *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Votre nom"
                        className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2 text-foreground">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre.email@exemple.com"
                        className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2 text-foreground">
                        Téléphone
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+225 XX XX XX XX XX"
                        className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium mb-2 text-foreground">
                        Sujet *
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Objet de votre demande"
                        className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2 text-foreground">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez votre projet ou votre demande..."
                        rows={6}
                        className="bg-background/50 border-border/50 focus:border-primary transition-colors resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
                      style={{
                        boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent), inset 0 1px 0 rgba(255,255,255, 0.1), inset 0 -1px 0 rgba(0,0,0, 0.1)',
                      }}
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer le Message
                    </Button>
                  </form>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </div>
  );
}